import { BarChart, Calculator, Calendar, CircleFadingPlus, CreditCard, Layout, MapPin, Receipt } from "lucide-react";

import {
  AnnouncementIcon,
  GBPIllustration,
  GoogleIcon,
  LinkIllustration,
  SMMIllustration,
  WebIcon,
  WebsiteIllustration,
} from "@/components/icons/HomePage";
import ABOUT_SECTIONS from "@/lib/constants/pages/aboutPageSections";
import CONTACT_SECTIONS from "@/lib/constants/pages/contactPageSections";
import HOME_SECTIONS from "@/lib/constants/pages/homePageSections";
import MISC_SECTIONS from "@/lib/constants/pages/miscSections";
import PPC_SECTIONS from "@/lib/constants/pages/ppcPageSections";
import { serviceProjectsSection, teamSection } from "@/lib/constants/sections/individualSections";

export const PRODUCT_CATEGORIES = {
  MARKETING_KIT: "MARKETING_KIT",
  CRM: "CRM",
} as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[keyof typeof PRODUCT_CATEGORIES];

type Product = {
  shortName: string;
  longName: string;
  demoImage: string;
  productKey: string;
  illustration: any;
  slug: string;
  alt: string;
  description: string;
  details: string[];
  icon: any;
  isComingSoon: boolean;
  className?: string;
  carouselIcon?: any;
  ctaText?: string;
  ctaLink?: string;
  excludeFromHomepage?: boolean;
  category: ProductCategory;
  navDescription?: string;
};

export const PRODUCT_TYPES = {
  WEBSITE: "WEBSITE",
  SOCIAL_MEDIA: "SOCIAL_MEDIA",
  GBP: "GBP",
  LOCAL_GEOTAGGING: "LOCAL_GEOTAGGING",
  ADS: "ADS",
  QUOTES: "QUOTES",
  REVIEWS: "REVIEWS",
  PAYMENTS: "PAYMENTS",
  CRM: "CRM",
  SCHEDULING: "SCHEDULING",
  INVOICING: "INVOICING",
} as const;

export const PRODUCTS: readonly Product[] = [
  // Marketing Kit products
  {
    shortName: "Website",
    longName: "SEO-Optimized Website",
    demoImage: "/website-demo.png",
    productKey: PRODUCT_TYPES.WEBSITE,
    illustration: WebsiteIllustration,
    carouselIcon: WebIcon,
    slug: "website-builder",
    alt: "How it works",
    description:
      "Effortlessly create professional websites designed to captivate and convert. No technical skills required—just your vision and our intuitive tools.",
    details: [
      "Easy drag-and-drop builder with smart, SEO-optimized templates",
      "Automatic metadata, clean code, and responsive design",
      "Boost your organic traffic with proven SEO features",
    ],
    icon: Layout,
    isComingSoon: false,
    className: "product-image-skew-left",
    category: PRODUCT_CATEGORIES.MARKETING_KIT,
    navDescription: "SEO-optimized website builder",
  },
  {
    shortName: "Social Media",
    longName: "Social Media Management",
    demoImage: "/social-media-demo.png",
    productKey: PRODUCT_TYPES.SOCIAL_MEDIA,
    illustration: SMMIllustration,
    carouselIcon: AnnouncementIcon,
    slug: "social-media-manager",
    alt: "Website Preview",
    description: "AI-powered content creation and scheduling for social media done for you",
    details: [
      "No more thinking about what or when to post",
      "No more logging into multiple platforms just for one post",
      "Available for Facebook, Google My Business, and Instagram",
    ],
    icon: CircleFadingPlus,
    isComingSoon: false,
    className: "product-image-skew-right",
    ctaText: "Watch a demo",
    ctaLink: "https://www.youtube.com/shorts/PQFUjEgDFtE",
    category: PRODUCT_CATEGORIES.MARKETING_KIT,
    navDescription: "AI-powered content & scheduling",
  },
  {
    shortName: "Google Profile",
    longName: "Google Profile Optimization",
    demoImage: "/GBPDemo-new.png",
    productKey: PRODUCT_TYPES.GBP,
    illustration: GBPIllustration,
    carouselIcon: GoogleIcon,
    slug: "google-business-profile-optimizer",
    alt: "Social Preview",
    description:
      "Boost your local visibility with a fully optimized Google Business Profile that attracts more customers.",
    details: [
      "Seamlessly update and manage your business details",
      "Respond to reviews and showcase your accolades effortlessly",
      "Improve your local search ranking with rich, accurate profile data",
    ],
    icon: BarChart,
    isComingSoon: false,
    ctaText: "Watch a demo",
    className: "w-full",
    ctaLink: "https://youtu.be/s9NPPdkZUd0",
    category: PRODUCT_CATEGORIES.MARKETING_KIT,
    navDescription: "Boost local visibility",
  },
  {
    shortName: "Local Geotagging",
    longName: "Local Geotagging & Showcase",
    demoImage: "/geotagging.png",
    productKey: PRODUCT_TYPES.LOCAL_GEOTAGGING,
    illustration: LinkIllustration,
    carouselIcon: MapPin,
    slug: "local-geotagging",
    alt: "Project map showcase",
    description:
      "Every project you complete gets pinned to a map, making it easy for homeowners to see your work in their neighborhood and trust you with theirs.",
    details: [
      "Auto-tag projects on a map as you add them",
      "Let customers filter by service type or neighborhood",
      "Turn your best reviews into stories that build trust",
      "Showcase your top work with embeddable website widgets",
    ],
    icon: MapPin,
    isComingSoon: false,
    className: "md:-rotate-1 shadow-md",
    category: PRODUCT_CATEGORIES.MARKETING_KIT,
    navDescription: "Map your work by neighborhood",
  },
  // CRM products
  {
    shortName: "Quoting",
    longName: "Quoting Software",
    demoImage: "/optional-line-items.png",
    productKey: PRODUCT_TYPES.QUOTES,
    illustration: WebsiteIllustration,
    carouselIcon: WebIcon,
    slug: "home-service-quoting-tool",
    alt: "Quote creation interface",
    description:
      "Create professional quotes, estimates, bids, and proposals in minutes—on-site or at your desk. Use voice to quote, a simple price book, optional line items, automated follow-ups, and instant online approvals.",
    details: [
      "Build quotes from your phone in minutes",
      "Voice-to-quote feature for fast on-site estimating",
      "Price book for consistent pricing across your team",
      "Optional line items to increase average ticket size",
      "Automated follow-ups to win back busy customers",
      "Online approvals customers can sign off in seconds",
    ],
    icon: Calculator,
    isComingSoon: false,
    excludeFromHomepage: true,
    ctaText: "Learn more",
    ctaLink: "/features/home-service-quoting-tool",
    category: PRODUCT_CATEGORIES.CRM,
    navDescription: "Fast quotes & estimates",
  },
  {
    shortName: "Scheduling",
    longName: "Scheduling & Dispatch",
    demoImage: "",
    productKey: PRODUCT_TYPES.SCHEDULING,
    illustration: WebsiteIllustration,
    carouselIcon: WebIcon,
    slug: "home-service-scheduling-software",
    alt: "Scheduling interface",
    description:
      "Drag-and-drop scheduling, crew assignment, automated reminders, and mobile dispatch for home service businesses. Keep your schedule full and your team on time.",
    details: [
      "Drag-and-drop scheduling to adjust instantly",
      "Assign jobs by crew/tech with availability visibility",
      "Automated appointment reminders reduce no-shows",
      "Mobile scheduling app for techs in the field",
    ],
    icon: Calendar,
    isComingSoon: false,
    excludeFromHomepage: true,
    ctaText: "Learn more",
    ctaLink: "/features/home-service-scheduling-software",
    category: PRODUCT_CATEGORIES.CRM,
    navDescription: "Job scheduling & dispatch",
  },
  {
    shortName: "Invoicing",
    longName: "Invoicing",
    demoImage: "",
    productKey: PRODUCT_TYPES.INVOICING,
    illustration: WebsiteIllustration,
    carouselIcon: WebIcon,
    slug: "invoicing",
    alt: "Invoicing interface",
    description: "Create and send professional invoices in seconds.",
    details: [],
    icon: Receipt,
    isComingSoon: true,
    excludeFromHomepage: true,
    category: PRODUCT_CATEGORIES.CRM,
    navDescription: "Professional invoicing",
  },
  {
    shortName: "Payments",
    longName: "Payment Acceptance",
    demoImage: "",
    productKey: PRODUCT_TYPES.PAYMENTS,
    illustration: WebsiteIllustration,
    carouselIcon: WebIcon,
    slug: "payments",
    alt: "Payments interface",
    description: "Accept payments online, in the field, or over the phone.",
    details: [],
    icon: CreditCard,
    isComingSoon: true,
    excludeFromHomepage: true,
    category: PRODUCT_CATEGORIES.CRM,
    navDescription: "Accept payments anywhere",
  },
] as const;

export const SITE_DOMAIN = process.env.NEXT_PUBLIC_API_HOST || "https://rebolthq.com";

export const DEFAULT_TIMEZONE = "America/New_York";

/**
 * US timezone options for user-facing selectors
 * Each entry has an IANA identifier and a human-friendly label
 */
export const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
] as const;

/** Valid IANA timezone values derived from US_TIMEZONES */
export const US_TIMEZONE_VALUES = US_TIMEZONES.map((tz) => tz.value);

export const ANALYSIS_CACHE_PREFIX = "site-analysis";

// this is used in allowing someone to select a section to insert into a page
export const INSERTABLE_SECTIONS = mergeSections(
  HOME_SECTIONS,
  serviceProjectsSection,
  teamSection,
  MISC_SECTIONS,
  PPC_SECTIONS
);

export const GENERAL_PAGES = [
  {
    name: "Home",
    slug: "",
    metaTitle: "Home Page",
    metaDescription:
      "We are a full-service landscaping company that provides everything from design to installation and maintenance.",
    sections: HOME_SECTIONS.map((s, idx) => ({ ...s, order: idx + 1 })),
  },
  {
    name: "Contact",
    slug: "contact-us",
    metaTitle: "Contact Us",
    metaDescription: "Get in touch with us to schedule a consultation or ask any questions you may have.",
    sections: CONTACT_SECTIONS,
  },
  {
    name: "About Us",
    slug: "about-us",
    metaTitle: "About Us",
    metaDescription:
      "We are a full-service landscaping company that provides everything from design to installation and maintenance.",
    sections: ABOUT_SECTIONS,
  },
];

export const STATES = [
  { name: "Wisconsin", abbreviation: "WI", lat: 44.5, lng: -89.5 },
  { name: "West Virginia", abbreviation: "WV", lat: 39, lng: -80.5 },
  { name: "Vermont", abbreviation: "VT", lat: 44, lng: -72.699997 },
  { name: "Texas", abbreviation: "TX", lat: 31, lng: -100 },
  { name: "South Dakota", abbreviation: "SD", lat: 44.5, lng: -100 },
  { name: "Rhode Island", abbreviation: "RI", lat: 41.742325, lng: -71.742332 },
  { name: "Oregon", abbreviation: "OR", lat: 44, lng: -120.5 },
  { name: "New York", abbreviation: "NY", lat: 43, lng: -75 },
  { name: "New Hampshire", abbreviation: "NH", lat: 44, lng: -71.5 },
  { name: "Nebraska", abbreviation: "NE", lat: 41.5, lng: -100 },
  { name: "Kansas", abbreviation: "KS", lat: 38.5, lng: -98 },
  { name: "Mississippi", abbreviation: "MS", lat: 33, lng: -90 },
  { name: "Illinois", abbreviation: "IL", lat: 40, lng: -89 },
  { name: "Delaware", abbreviation: "DE", lat: 39, lng: -75.5 },
  { name: "Connecticut", abbreviation: "CT", lat: 41.599998, lng: -72.699997 },
  { name: "Arkansas", abbreviation: "AR", lat: 34.799999, lng: -92.199997 },
  { name: "Indiana", abbreviation: "IN", lat: 40.273502, lng: -86.126976 },
  { name: "Missouri", abbreviation: "MO", lat: 38.573936, lng: -92.60376 },
  { name: "Florida", abbreviation: "FL", lat: 27.994402, lng: -81.760254 },
  { name: "Nevada", abbreviation: "NV", lat: 39.876019, lng: -117.224121 },
  { name: "Maine", abbreviation: "ME", lat: 45.367584, lng: -68.972168 },
  { name: "Michigan", abbreviation: "MI", lat: 44.182205, lng: -84.506836 },
  { name: "Georgia", abbreviation: "GA", lat: 33.247875, lng: -83.441162 },
  { name: "Hawaii", abbreviation: "HI", lat: 19.741755, lng: -155.844437 },
  { name: "Alaska", abbreviation: "AK", lat: 66.160507, lng: -153.369141 },
  { name: "Tennessee", abbreviation: "TN", lat: 35.860119, lng: -86.660156 },
  { name: "Virginia", abbreviation: "VA", lat: 37.926868, lng: -78.024902 },
  { name: "New Jersey", abbreviation: "NJ", lat: 39.833851, lng: -74.871826 },
  { name: "Kentucky", abbreviation: "KY", lat: 37.839333, lng: -84.27002 },
  {
    name: "North Dakota",
    abbreviation: "ND",
    lat: 47.650589,
    lng: -100.437012,
  },
  { name: "Minnesota", abbreviation: "MN", lat: 46.39241, lng: -94.63623 },
  { name: "Oklahoma", abbreviation: "OK", lat: 36.084621, lng: -96.921387 },
  { name: "Montana", abbreviation: "MT", lat: 46.96526, lng: -109.533691 },
  { name: "Washington", abbreviation: "WA", lat: 47.751076, lng: -120.740135 },
  { name: "Utah", abbreviation: "UT", lat: 39.41922, lng: -111.950684 },
  { name: "Colorado", abbreviation: "CO", lat: 39.113014, lng: -105.358887 },
  { name: "Ohio", abbreviation: "OH", lat: 40.367474, lng: -82.996216 },
  { name: "Alabama", abbreviation: "AL", lat: 32.31823, lng: -86.902298 },
  { name: "Iowa", abbreviation: "IA", lat: 42.032974, lng: -93.581543 },
  { name: "New Mexico", abbreviation: "NM", lat: 34.307144, lng: -106.018066 },
  {
    name: "South Carolina",
    abbreviation: "SC",
    lat: 33.836082,
    lng: -81.163727,
  },
  { name: "Pennsylvania", abbreviation: "PA", lat: 41.203323, lng: -77.194527 },
  { name: "Arizona", abbreviation: "AZ", lat: 34.048927, lng: -111.093735 },
  { name: "Maryland", abbreviation: "MD", lat: 39.045753, lng: -76.641273 },
  {
    name: "Massachusetts",
    abbreviation: "MA",
    lat: 42.407211,
    lng: -71.382439,
  },
  { name: "California", abbreviation: "CA", lat: 36.778259, lng: -119.417931 },
  { name: "Idaho", abbreviation: "ID", lat: 44.068203, lng: -114.742043 },
  { name: "Wyoming", abbreviation: "WY", lat: 43.07597, lng: -107.290283 },
  {
    name: "North Carolina",
    abbreviation: "NC",
    lat: 35.782169,
    lng: -80.793457,
  },
  { name: "Louisiana", abbreviation: "LA", lat: 30.39183, lng: -92.329102 },
];

export type State = (typeof STATES)[number];

export const DEFAULT_REACT_TAG_STYLES = {
  root: "react-tags",
  rootIsActive: "is-active",
  rootIsDisabled: "is-disabled",
  rootIsInvalid: "is-invalid",
  label: "react-tags__label",
  tagList: "react-tags__list",
  tagListItem: "react-tags__list-item",
  tag: "react-tags__tag",
  tagName: "react-tags__tag-name",
  comboBox: "react-tags__combobox",
  input: "react-tags__combobox-input",
  listBox: "react-tags__listbox",
  option: "react-tags__listbox-option",
  optionIsActive: "is-active",
  highlight: "react-tags__listbox-option-highlight",
};

export const FACEBOOK_URL = "https://www.facebook.com/getrebolt/";
export const LINKEDIN_URL = "https://www.linkedin.com/company/rebolthq";
export const INSTAGRAM_URL = "https://www.instagram.com/getrebolt/";
export const TWITTER_URL = "https://twitter.com/getrebolt";
export const YOUTUBE_URL = "https://www.youtube.com/@getrebolt";

export const INDUSTRY_LOCAL_STORAGE_KEY = "br-industry";

export const MIN_REVIEWS = 9;

// Pagination constants
export const LEADS_PER_PAGE = 50;

function mergeSections(...sectionArrays: any[]) {
  const sectionMap = new Map();

  sectionArrays.flat().forEach((section) => {
    sectionMap.set(section.type, section);
  });

  return Array.from(sectionMap.values());
}

export const REVIEW_ORDER_BY = [
  { starred: "desc" },
  { source: "asc" }, // GOOGLE before MANUAL
  { createdAt: "desc" },
  { id: "desc" },
];

export const INTERCOM_APP_ID = "vuax6o7x";
